import { IPageAction, IPageControl } from "../base/models";
import React, { createContext, useContext, useReducer } from "react";

import { DesignerMode } from "../design/types";
import { IDataSource } from "../base/types";

interface IPageItemState<T extends IPageControl | IPageAction> {
    element?: T;
    dataSource?: IDataSource;
}

interface IPageItemEditState<T extends IPageControl | IPageAction> extends IPageItemState<T> {
    designerMode: DesignerMode;
}

interface IPageControlState extends IPageItemState<IPageControl> { }
interface IPageActionState extends IPageItemState<IPageAction> { }

interface IPageControlEditState extends IPageItemEditState<IPageControl> { }
interface IPageActionEditState extends IPageItemEditState<IPageAction> { }

interface IDesignContextState {
    control: IPageControlEditState;
    action: IPageActionEditState;
}

const initialDesignContextState: IDesignContextState = {
    control: { designerMode: DesignerMode.View },
    action: { designerMode: DesignerMode.View },
}

type DesignAction =
    { type: "SET_CONTROL_STATE", payload: IPageControlEditState } |
    { type: "SET_ACTION_STATE", payload: IPageActionEditState }

const designReducer = (state: IDesignContextState, action: DesignAction): IDesignContextState => {
    switch (action.type) {
        case "SET_CONTROL_STATE":
            return { ...state, control: action.payload };
        case "SET_ACTION_STATE":
            return { ...state, action: action.payload };
        default:
            return state;
    }
};

interface IPageControlContext extends IPageControlEditState {
    setContext: (context: IPageControlState) => void;
    clearContext: () => void,
    setEditMode: (mode: DesignerMode) => void;
}

interface IPageActionContext extends IPageActionEditState {
    setContext: (context: IPageActionState) => void;
    clearContext: () => void,
    setEditMode: (mode: DesignerMode) => void;
}

interface IDesignContextModel {
    control: IPageControlContext;
    action: IPageActionContext;
}

const initialDesignContext: IDesignContextModel = {
    control: {
        designerMode: DesignerMode.View,
        setContext: () => { },
        clearContext: () => { },
        setEditMode: () => { },
    },
    action: {
        designerMode: DesignerMode.View,
        setContext: () => { },
        clearContext: () => { },
        setEditMode: () => { },
    },
};

export const DesignContext = createContext<IDesignContextModel>(initialDesignContext);

export function useDesignContext() {
    return useContext(DesignContext);
}

export const DesignContextProvider = ({ children }: { children?: React.ReactNode }) => {
    const [state, dispatch] = useReducer(designReducer, initialDesignContextState);

    const setControlContext = (context: IPageControlState) => {
        const { designerMode: mode } = state.control
        dispatch({ type: "SET_CONTROL_STATE", payload: { ...context, designerMode: mode } });
    };

    const clearControlContext = () => {
        const { designerMode: mode } = state.control
        dispatch({ type: "SET_CONTROL_STATE", payload: { designerMode: mode } });
    };

    const setControlEditMode = (mode: DesignerMode) => {
        const { control } = state
        dispatch({ type: "SET_CONTROL_STATE", payload: { ...control, designerMode: mode } });
    }

    const setActionContext = (context: IPageActionState) => {
        const { designerMode: mode } = state.action
        dispatch({ type: "SET_ACTION_STATE", payload: { ...context, designerMode: mode } });
    };

    const clearActionContext = () => {
        const { designerMode: mode } = state.action
        dispatch({ type: "SET_ACTION_STATE", payload: { designerMode: mode } });
    };

    const setActionEditMode = (mode: DesignerMode) => {
        const { action } = state
        dispatch({ type: "SET_ACTION_STATE", payload: { ...action, designerMode: mode } });
    };

    return (
        <DesignContext.Provider value={{
            control: {
                ...state.control,
                setContext: setControlContext,
                clearContext: clearControlContext,
                setEditMode: setControlEditMode
            },
            action: {
                ...state.action,
                setContext: setActionContext,
                clearContext: clearActionContext,
                setEditMode: setActionEditMode
            }
        }}>
            {children}
        </DesignContext.Provider>
    );
};
